import type { Component } from 'vue';
import type { IconComponent } from '../internal';

/**
 * Sentinel group name for a section's primary („function“) actions. Items in
 * this group render as the full-width buttons at the top of an
 * `NxActionGroups`; every other group renders as a labelled sub-grid below
 * them. Same value as the ERP's `FUNCTION_GROUP`, so maps built for the ERP
 * sidebar keep working.
 */
export const NX_FUNCTION_GROUP = '__FUNCTION_GROUP__';

/** One action of a record sidebar (a button in a list, a toolbar or a group). */
export interface NxActionElement {
  /**
   * Stable identity for `v-for` keying. Set it whenever the list is built in a
   * `computed` that can DROP entries: with a positional key Vue would recycle
   * the dropped entry's button — focus and hover included — for the entry that
   * moved up into its slot. Optional, because a fixed-length list cannot hit
   * that.
   */
  id?: string;
  label: string;
  icon?: {
    /**
     * An `@blpde/nx-icons` icon (takes `filled`/`color`) or any other icon
     * component, e.g. a Lucide glyph the app already uses.
     */
    component: IconComponent | Component;
    filled?: boolean;
    color?: string;
  };
  /** Invoked when the user picks this entry. */
  onClick: () => void;
  /**
   * Renders the entry greyed out and unclickable. Keep the guard in `onClick`
   * as well: the flag decides what the user sees, not what the handler may do.
   */
  disabled?: boolean;
  /**
   * `danger` draws the entry as a destructive action (outline-danger), e.g.
   * „Löschen“. Left out, the entry keeps the plain outline.
   */
  tone?: 'danger';
  /** Value for the button's `data-test` attribute, where a test needs one specific action. */
  dataTest?: string;
  /**
   * Group name inside `NxActionGroups`; `NX_FUNCTION_GROUP` for the primary
   * buttons. In an `NxActionToolbar` with `groupSeparators`, a change of
   * `group` between two neighbours draws a separator.
   */
  group?: string;
}
